import React, { useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import CustomerDetail from './CustomerDetail';
import { Customer } from './types';
import { PortalWithState } from 'react-portal';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader from '../ThirdPane/ThirdPaneHeader';
import Button from '../Button/Button';
import CustomerEditModal from './CustomerEditModal';
import useMode from '../../hooks/useMode';
import useNotify from '../../hooks/useNotify';
import { ApolloError } from '@apollo/client/errors';
import {
  CUSTOMER_FIELDS_WATER_SUPPLIER,
  gql as waterSupplier_gql,
} from '@/modes/waterSupplier/gql';
import {
  gql as company_gql,
  CUSTOMER_FIELDS_COMPANY,
} from '@/modes/company/gql';
import RequirePerms from '@/components/RequirePerms';

type CustomerThirdPaneProps = {
  customerId?: string;
  prevEnabled: boolean;
  nextEnabled: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  onToggle?: (visible: boolean) => void;
};

const SET_CUSTOMER_STATUS = company_gql(`#graphql
  mutation SetCustomerStatus($customerId: ID!, $status: ContractStatus!) {
    setCustomerStatus(customerId: $customerId, status: $status) {
      id
      status
    }
  }
`);

const GET_CUSTOMER_DETAIL_WATER_SUPPLIER = waterSupplier_gql(
  `#graphql
  query GetCustomerDetail($where: JSON) {
    customers(where: $where) {
      ...CustomerFields
    }
  }
`,
  [CUSTOMER_FIELDS_WATER_SUPPLIER]
);

const GET_CUSTOMER_DETAIL_COMPANY = company_gql(
  `#graphql
  query GetCustomerDetail($where: JSON) {
    customers(where: $where) {
      ...CustomerFields
    }
  }
`,
  [CUSTOMER_FIELDS_COMPANY]
);

export default function CustomerThirdPane({
  customerId,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: CustomerThirdPaneProps): JSX.Element {
  const mode = useMode();

  const GET_CUSTOMER_DETAIL =
    mode === 'water-supplier'
      ? GET_CUSTOMER_DETAIL_WATER_SUPPLIER
      : GET_CUSTOMER_DETAIL_COMPANY;

  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
  } = useQuery<{
    customers: Customer[];
  }>(GET_CUSTOMER_DETAIL, {
    skip: !customerId,
    variables: { where: { id: customerId || null } },
  });

  const { showError } = useNotify();

  const customer = itemData?.customers[0];

  const [
    setCustomerStatus,
    { data, loading: mutationLoading, error: mutationError },
  ] = useMutation(SET_CUSTOMER_STATUS, {
    onError: useCallback((error: ApolloError) => {
      showError('Error setting customer status', error);
    }, []),
  });

  const activateDeactivate = () => {
    if (!customer) {
      return;
    }
    customer.status === 'ACTIVE'
      ? setCustomerStatus({
          variables: { customerId: customer.id, status: 'INACTIVE' },
        })
      : setCustomerStatus({
          variables: { customerId: customer.id, status: 'ACTIVE' },
        });
  };

  return (
    <>
      <ThirdPane visible={!!customerId}>
        <ThirdPaneHeader
          onToggle={onToggle}
          title={customer ? customer.profile.name : 'Loading...'}
          onPrev={onPrev}
          onNext={onNext}
          prevEnabled={prevEnabled}
          nextEnabled={nextEnabled}
        >
          <RequirePerms customers="UPDATE">
            <Button
              mr={3}
              variant={customer?.status === 'ACTIVE' ? 'danger' : 'outline'}
              onClick={activateDeactivate}
            >
              {customer?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
            </Button>
          </RequirePerms>
          {customer && (
            <RequirePerms customers="UPDATE">
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <Button
                      mr={10}
                      icon="edit"
                      variant="outline"
                      onClick={openPortal}
                    >
                      Edit
                    </Button>
                    {portal(
                      <CustomerEditModal
                        action="edit"
                        perms={customer.profile.perms}
                        onClose={closePortal}
                        customer={customer}
                      />
                    )}
                  </>
                )}
              </PortalWithState>
            </RequirePerms>
          )}
        </ThirdPaneHeader>
        <ThirdPaneContent>
          {customer && <CustomerDetail customerDetail={customer} />}
        </ThirdPaneContent>
      </ThirdPane>
    </>
  );
}
